import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storageSession from "redux-persist/es/storage/session";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import { MarkLabels } from "../common/leaf/LeafSlider/LeafSlider";
import { ECCalculateResponse, ECSettingsData } from "../services/EarningsCalculator/earningCalculatorAPI.types";
import { BONUS_KPI } from "../components/EarningsCalculator/EarningsCalculatorIncomeBreakdown/EarningsCalculatorIncomeBreakdown";
import {
  addNewLegs,
  ECComputedData,
  extractBronzeRequirements,
  getABOLegsWithGPVBase,
  getBronzeTargetStatus,
  getCalculateAPIData,
  getFakeAffABONumber,
  getSettings,
  getSettingsAPIData,
  updateLegsGPV,
} from "./model/earningCalculator";
import { setShellConfig } from "./boot";

export interface EarningSummaryBody {
  PersonalPV: number;
  VCSPV: number;
}

export interface BronzeConsistencyPaymentBody {
  ConsistencyPayment: {
    CurrentAt: number;
  };
  FixedAmountPayment: {
    CurrentAt: number;
  };
}

export interface IncomeBreakdownBody {
  BronzeFoundation: BronzeConsistencyPaymentBody;
  BronzeBuilder: BronzeConsistencyPaymentBody;
}

interface ABOName {
  ABOName: string;
  ABOLocalName: string;
  ISOLanguage: string;
  PrivacyFlag: boolean;
}

export interface ABOLegBody {
  Removeable: boolean;
  AffABO: string;
  Name: ABOName;
  GroupPV: number;
  GroupPVBase?: number;
}

export interface CSIBody {
  Active: boolean;
  Multiplier: number[];
  ReqMinGPV: number;
  MaxCSIPercent: number;
}

export interface LegsPercentageBody {
  Required: boolean;
  Target: number;
  Percent: number;
}

export interface PerformanceBonusBody {
  Required: boolean;
  RequiredGPV: number;
  Target: number;
}

export interface VCSBonusLegsBody {
  Required: boolean;
  Target: number;
  TargetVCSPV: number;
}

export interface RequirementsBody {
  LegsPercentage: LegsPercentageBody;
  PerformanceBonus: PerformanceBonusBody;
  VCSBonusLegs: VCSBonusLegsBody;
}

export interface ScheduleDataBody {
  Target: number;
  Bonus: {
    Actual: number;
    Formatted: string;
  };
}

export interface ConsistencyPaymentBody {
  Active: boolean;
  Schedule: ScheduleDataBody[];
}

export interface BronzeDetailsDataBody {
  Active: boolean;
  Multiplier: number;
  Requirements: RequirementsBody;
  ConsistencyPayment: ConsistencyPaymentBody;
}

export interface BonusDetail {
  Volume: number;
  Percent: number;
}

export interface BonusSchedule {
  ISOCountry: string;
  ISOCurrency: string;
  BonusDetails: BonusDetail[];
}

export interface ABOLegsData {
  MaxLegs: number;
  Min: number;
  Max: number;
  Marks: {
    Label: string;
    Value: number;
  }[];
  Labels: MarkLabels[];
}

export interface SettingsBody {
  IsExceededMaxValue: boolean;
  CSI: CSIBody;
  BronzeBuilder: BronzeDetailsDataBody;
  BronzeFoundation: BronzeDetailsDataBody;
  BonusSchedule: BonusSchedule;
  ABOLegs: ABOLegsData;
  PvBvRatio: number;
}

export interface EarningCalculator {
  EarningSummary: EarningSummaryBody;
  IncomeBreakdown: IncomeBreakdownBody;
  ABOLegs: ABOLegBody[];
  Settings: SettingsBody;
  ECComputedData: ECComputedData;
  Initialized: boolean;
  IsTrainingMode: boolean;
  Recalculate: boolean;
  IsBFITabActive: boolean;
  IsBBITabActive: boolean;
  CurrentCountry: string;
}

export const initialState: EarningCalculator = {
  EarningSummary: {
    PersonalPV: 0,
    VCSPV: 0,
  },
  IncomeBreakdown: {
    BronzeFoundation: {
      ConsistencyPayment: {
        CurrentAt: 0,
      },
      FixedAmountPayment: {
        CurrentAt: 0,
      },
    },
    BronzeBuilder: {
      ConsistencyPayment: {
        CurrentAt: 0,
      },
      FixedAmountPayment: {
        CurrentAt: 0,
      },
    },
  },
  ABOLegs: [],
  Settings: {
    IsExceededMaxValue: false,
    CSI: {
      Active: false,
      Multiplier: [],
      ReqMinGPV: 0,
      MaxCSIPercent: 0,
    },
    BronzeBuilder: {
      Active: false,
      Multiplier: 0,
      Requirements: {
        LegsPercentage: {
          Required: false,
          Target: 0,
          Percent: 0,
        },
        PerformanceBonus: {
          Required: false,
          RequiredGPV: 0,
          Target: 0,
        },
        VCSBonusLegs: {
          Required: false,
          Target: 0,
          TargetVCSPV: 0,
        },
      },
      ConsistencyPayment: {
        Active: false,
        Schedule: [],
      },
    },
    BronzeFoundation: {
      Active: false,
      Multiplier: 0,
      Requirements: {
        LegsPercentage: {
          Required: false,
          Target: 0,
          Percent: 0,
        },
        PerformanceBonus: {
          Required: false,
          RequiredGPV: 0,
          Target: 0,
        },
        VCSBonusLegs: {
          Required: false,
          Target: 0,
          TargetVCSPV: 0,
        },
      },
      ConsistencyPayment: {
        Active: false,
        Schedule: [],
      },
    },
    BonusSchedule: {
      ISOCountry: "",
      ISOCurrency: "",
      BonusDetails: [],
    },
    ABOLegs: {
      MaxLegs: 0,
      Min: 0,
      Max: 100,
      Marks: [
        {
          Label: "GPV",
          Value: 0,
        },
      ],
      Labels: [],
    },
    PvBvRatio: 0,
  },
  ECComputedData: {
    EarningSummary: {
      PersonalPV: {
        Actual: 0,
        Formatted: "0",
      },
      VCSPV: {
        Actual: 0,
        Formatted: "0",
      },
      PersonalBV: {
        Actual: 0,
        Formatted: "0",
      },
      GroupPV: {
        Actual: 0,
        Formatted: "0",
      },
      PerfBonusLevel: {
        Actual: 0,
        Formatted: "0",
      },
      Earnings: {
        Actual: 0,
        Formatted: "0",
      },
    },
    IncomeBreakdown: {
      Percentage: [],
      PerfBonusTotal: {
        Personal: {
          Basis: {
            Actual: 0,
            Formatted: "0",
          },
          Multiplier: {
            Actual: 0,
            Formatted: "0",
          },
          Bonus: {
            Actual: 0,
            Formatted: "0",
          },
        },
        Differential: {
          BonusAmount: {
            Actual: 0,
            Formatted: "0",
          },
        },
        BonusAmount: {
          Actual: 0,
          Formatted: "0",
        },
      },
      CSI: {
        IsQualified: false,
        Basis: {
          Actual: 0,
          Formatted: "0",
        },
        Multiplier: {
          Actual: 0,
          Formatted: "0",
        },
        Bonus: {
          Actual: 0,
          Formatted: "0",
        },
      },
      BronzeFoundation: {
        IsQualified: false,
        Basis: {
          Actual: 0,
          Formatted: "0",
        },
        Multiplier: {
          Actual: 0,
          Formatted: "0",
        },
        Bonus: {
          Actual: 0,
          Formatted: "0",
        },
        ConsistencyPayment: {
          CurrentAt: 0,
          Bonus: {
            Actual: 0,
            Formatted: "0",
          },
        },
        FixedAmountPayment: {
          CurrentAt: 0,
        },
        TotalPayment: {
          Actual: 0,
          Formatted: "0",
        },
      },
      BronzeBuilder: {
        IsQualified: false,
        Basis: {
          Actual: 0,
          Formatted: "0",
        },
        Multiplier: {
          Actual: 0,
          Formatted: "0",
        },
        Bonus: {
          Actual: 0,
          Formatted: "0",
        },
        ConsistencyPayment: {
          CurrentAt: 0,
          Bonus: {
            Actual: 0,
            Formatted: "0",
          },
        },
        FixedAmountPayment: {
          CurrentAt: 0,
        },
        TotalPayment: {
          Actual: 0,
          Formatted: "0",
        },
      },
      IncomeBreakdownTotal: {
        Actual: 0,
        Formatted: "0",
      },
    },
    ABOLegs: [],
    Settings: {
      PersonalPV: {
        Min: 0,
        Max: 100,
        Marks: [],
      },
      CSI: {
        Active: false,
        GPV: {
          Min: 0,
          Max: 100,
          Marks: [],
        },
        Multiplier: [],
        ReqMinGPV: 0,
        MaxCSIPercent: {
          Actual: 0,
          Formatted: "0",
        },
      },
      ABOLegs: {
        MaxLegs: 0,
        Min: 0,
        Max: 100,
        Marks: [],
        Labels: [],
      },
      BronzeBuilder: {
        Active: false,
        Multiplier: 0,
        Requirements: {
          LegsPercentage: {
            Required: false,
            Target: 0,
            Percent: 0,
            RequiredGPV: 0,
          },
          PerformanceBonus: {
            Required: false,
            RequiredGPV: 0,
            Target: 0,
          },
          VCSBonusLegs: {
            Required: false,
            Target: 0,
            TargetVCSPV: 0,
          },
          PersonalVolume: {
            Required: false,
            Target: 0,
          },
        },
        ConsistencyPayment: {
          Active: false,
          Schedule: [],
        },
        FixedAmountPayment: {
          Active: false,
          Schedule: [],
        },
      },
      BronzeFoundation: {
        Active: false,
        Multiplier: 0,
        Requirements: {
          LegsPercentage: {
            Required: false,
            Target: 0,
            Percent: 0,
            RequiredGPV: 0,
          },
          PerformanceBonus: {
            Required: false,
            RequiredGPV: 0,
            Target: 0,
          },
          VCSBonusLegs: {
            Required: false,
            Target: 0,
            TargetVCSPV: 0,
          },
          PersonalVolume: {
            Required: false,
            Target: 0,
          },
        },
        ConsistencyPayment: {
          Active: false,
          Schedule: [],
        },
        FixedAmountPayment: {
          Active: false,
          Schedule: [],
        },
      },
      BonusSchedule: {
        ISOCountry: "",
        ISOCurrency: "",
        BonusDetails: [],
      },
      IsExceededMaxValue: false,
      PvBvRatio: 0,
    },
    StaticData: {
      PersonalPV: {
        Actual: 0,
        Formatted: "0",
      },
      VCSPV: {
        Actual: 0,
        Formatted: "0",
      },
      IsBFIQualified: false,
      IsBBIQualified: false,
    },
  },
  Initialized: false,
  IsTrainingMode: false,
  Recalculate: false,
  IsBFITabActive: false,
  IsBBITabActive: false,
  CurrentCountry: "",
};

const { actions, reducer } = createSlice({
  name: "earningCalculator",
  initialState,
  reducers: {
    initializeSettings: (state, action: PayloadAction<{ settingsAPIData: ECSettingsData }>) => {
      const { settingsAPIData } = action.payload;
      const currentState = getSettingsAPIData(settingsAPIData);
      const bronzeTargetStatus = getBronzeTargetStatus(
        currentState.Settings,
        currentState.EarningSummary,
        currentState.ABOLegs,
      );

      if (state.Initialized === false) {
        return {
          ...state,
          EarningSummary: {
            PersonalPV: currentState.EarningSummary.PersonalPV.Actual,
            VCSPV: currentState.EarningSummary.VCSPV.Actual,
          },
          IncomeBreakdown: {
            BronzeFoundation: {
              ConsistencyPayment: {
                CurrentAt: currentState.IncomeBreakdown.BronzeFoundation.ConsistencyPayment.CurrentAt,
              },
              FixedAmountPayment: {
                CurrentAt: currentState.IncomeBreakdown.BronzeFoundation.FixedAmountPayment.CurrentAt,
              },
            },
            BronzeBuilder: {
              ConsistencyPayment: {
                CurrentAt: currentState.IncomeBreakdown.BronzeBuilder.ConsistencyPayment.CurrentAt,
              },
              FixedAmountPayment: {
                CurrentAt: currentState.IncomeBreakdown.BronzeBuilder.FixedAmountPayment.CurrentAt,
              },
            },
          },
          ABOLegs: getABOLegsWithGPVBase(currentState.ABOLegs),
          Settings: getSettings(currentState.Settings),
          ECComputedData: currentState,
          Initialized: true,
          Recalculate: false,
          ...bronzeTargetStatus,
        };
      }
      return {
        ...state,
        ...bronzeTargetStatus,
      };
    },
    updateCalculatedData: (state, action: PayloadAction<ECCalculateResponse>) => {
      const calculatedData = getCalculateAPIData(action.payload);
      const settings = state.ECComputedData.Settings;
      const bronzeTargetStatus = getBronzeTargetStatus(settings, calculatedData.EarningSummary, calculatedData.ABOLegs);

      return {
        ...state,
        ECComputedData: {
          ...state.ECComputedData,
          EarningSummary: calculatedData.EarningSummary,
          IncomeBreakdown: calculatedData.IncomeBreakdown,
          ABOLegs: calculatedData.ABOLegs?.length > 0 ? calculatedData.ABOLegs : [],
        },
        Recalculate: false,
        ...bronzeTargetStatus,
      };
    },
    updatePersonalPV: (state, action: PayloadAction<number>) => ({
      ...state,
      EarningSummary: {
        ...state.EarningSummary,
        PersonalPV: action.payload,
      },
      Recalculate: true,
    }),
    updateVCSPV: (state, action: PayloadAction<number>) => ({
      ...state,
      EarningSummary: {
        ...state.EarningSummary,
        VCSPV: action.payload,
      },
      Recalculate: true,
    }),
    addABOLeg: (state) => {
      const newABO: ABOLegBody = {
        Removeable: true,
        AffABO: `${getFakeAffABONumber()}`,
        GroupPV: 0,
        GroupPVBase: 0,
        Name: {
          ABOName: "",
          ABOLocalName: "",
          ISOLanguage: "",
          PrivacyFlag: false,
        },
      };
      const { ABOLegs } = state;

      return {
        ...state,
        ABOLegs: [...ABOLegs, newABO],
        Recalculate: false,
      };
    },
    updateABOLegs: (state, action: PayloadAction<ABOLegBody>) => {
      const { ABOLegs } = state;
      return {
        ...state,
        ABOLegs: ABOLegs.map((aboLeg) => (aboLeg.AffABO === action.payload.AffABO ? action.payload : aboLeg)),
        Recalculate: true,
      };
    },
    updateBronzeTarget: (state, action: PayloadAction<string>) => {
      const isBFITarget = action.payload === BONUS_KPI.BFI;

      const { ABOLegs, EarningSummary } = state;
      const { Settings } = state.ECComputedData;

      const bronzeSetting = isBFITarget ? Settings.BronzeFoundation : Settings.BronzeBuilder;
      const { targetVCSPV, pvTarget, legsTarget, requiredGPV } = extractBronzeRequirements(bronzeSetting);

      const newABOLegs = addNewLegs(ABOLegs, legsTarget);
      const aboLegsWithRequiredGPV = updateLegsGPV(newABOLegs, legsTarget, requiredGPV);

      const personalPV = EarningSummary.PersonalPV >= pvTarget ? EarningSummary.PersonalPV : pvTarget;
      const vcsPV = EarningSummary.VCSPV >= targetVCSPV ? EarningSummary.VCSPV : targetVCSPV;

      return {
        ...state,
        ABOLegs: aboLegsWithRequiredGPV,
        EarningSummary: {
          ...state.EarningSummary,
          VCSPV: vcsPV,
          PersonalPV: personalPV,
        },
        Recalculate: true,
      };
    },
    removeABOLeg: (state, action: PayloadAction<ABOLegBody>) => ({
      ...state,
      ABOLegs: state.ABOLegs.filter((aboLeg) => aboLeg.AffABO !== action.payload.AffABO),
      Recalculate: true,
    }),
    updateTrainingMode: (state) => ({
      ...state,
      IsTrainingMode: !state.IsTrainingMode,
      Initialized: false,
      Recalculate: false,
    }),
    setSelectedBFIOptionId: (state, action: PayloadAction<number>) => ({
      ...state,
      IncomeBreakdown: {
        ...state.IncomeBreakdown,
        BronzeFoundation: {
          ...state.IncomeBreakdown.BronzeFoundation,
          ConsistencyPayment: {
            CurrentAt: action.payload,
          },
        },
      },
      Recalculate: true,
    }),
    setSelectedBBIOptionId: (state, action: PayloadAction<number>) => ({
      ...state,
      IncomeBreakdown: {
        ...state.IncomeBreakdown,
        BronzeBuilder: {
          ...state.IncomeBreakdown.BronzeBuilder,
          ConsistencyPayment: {
            CurrentAt: action.payload,
          },
        },
      },
      Recalculate: true,
    }),
    setBFIFixedPaymentCurrentAt: (state, action: PayloadAction<number>) => ({
      ...state,
      IncomeBreakdown: {
        ...state.IncomeBreakdown,
        BronzeFoundation: {
          ...state.IncomeBreakdown.BronzeFoundation,
          FixedAmountPayment: {
            CurrentAt: action.payload,
          },
        },
      },
      Recalculate: true,
    }),
    setBBIFixedPaymentCurrentAt: (state, action: PayloadAction<number>) => ({
      ...state,
      IncomeBreakdown: {
        ...state.IncomeBreakdown,
        BronzeBuilder: {
          ...state.IncomeBreakdown.BronzeBuilder,
          FixedAmountPayment: {
            CurrentAt: action.payload,
          },
        },
      },
      Recalculate: true,
    }),
    activateBFITab: (state) => ({
      ...state,
      IsBFITabActive: true,
      IsBBITabActive: false,
    }),
    activateBBITab: (state) => ({
      ...state,
      IsBBITabActive: true,
      IsBFITabActive: false,
    }),
  },
  extraReducers: (builder) => {
    // Side effect to observe when the setShellConfig state updates — specifically for the Earning Calculator (EC) use case, this occurs when the business is changed.
    builder.addCase(setShellConfig, (state, { payload }) => {
      const { isoCurrencyCode = "", aboInfo } = payload || {};
      const { isoCountryCode = "" } = aboInfo || {};

      const country = `${isoCountryCode}:${isoCurrencyCode}`;
      const changeBusiness = country !== state.CurrentCountry;

      return changeBusiness
        ? {
            ...state,
            CurrentCountry: country,
            Initialized: false,
          }
        : state;
    });
  },
});

export const {
  initializeSettings,
  updateCalculatedData,
  updatePersonalPV,
  updateVCSPV,
  addABOLeg,
  updateABOLegs,
  updateBronzeTarget,
  removeABOLeg,
  updateTrainingMode,
  setSelectedBFIOptionId,
  setSelectedBBIOptionId,
  activateBFITab,
  activateBBITab,
  setBFIFixedPaymentCurrentAt,
  setBBIFixedPaymentCurrentAt,
} = actions;

const ecPersistConfig = {
  key: "earningCalculator",
  storage: storageSession,
  stateReconciler: autoMergeLevel2,
};

export default persistReducer<EarningCalculator>(ecPersistConfig, reducer);
